import dayjs from "dayjs";
import {
  IGift,
  IGiftEligibilityConditions,
  IGiftIdConditions,
  IGiftIdValue,
  IGiftNamedId,
  IGiftItemDetails,
} from "@/api_framework/api_modals/GiftConfig";
import {
  GIFT_PET_SPECIES_ITEMS,
  GIFT_TIME_UNIT_SECONDS,
  GIFT_TYPE_DETAILS_KEY,
  GiftTimeUnit,
  GiftIdConditionKey,
  IGiftEligibilityForm,
  IGiftFormFields,
  IGiftIdConditionForm,
  IGiftItemFormRow,
  isGiftType,
} from "./interface";

// Form dates are stored as "YYYY-MM-DD", the format PWLCalendar's defaultSelectedDate/disable props read.
export const GIFT_FORM_DATE_FORMAT = "YYYY-MM-DD";

/** The API now sends dates as "YYYY-MM-DD"; older responses sent epoch seconds, so both are read. */
export const toDateInput = (value: string | number | null | undefined): string => {
  if (typeof value === "number") return value ? dayjs.unix(value).format(GIFT_FORM_DATE_FORMAT) : "";
  return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
};

const idOf = (value: IGiftIdValue): string => (typeof value === "string" ? value : String(value?.id ?? ""));

const toIdList = (values: IGiftIdValue[] | null | undefined): string[] => (values ?? []).map(idOf).filter(Boolean);

/** Collects every {id, name} pair in a fetched gift so chips can show names. */
const collectIdNames = (gift: IGift): Record<string, string> => {
  const names: Record<string, string> = {};
  const add = (values: IGiftIdValue[] | null | undefined) =>
    (values ?? []).forEach((value) => {
      if (typeof value !== "string" && value?.id && value.name) names[String(value.id)] = value.name;
    });
  Object.values(gift.eligibility_conditions ?? {}).forEach((condition) => {
    if (condition && "included_ids" in condition) {
      add(condition.included_ids);
      add(condition.excluded_ids);
    }
  });
  const details = gift.gift_details;
  [details?.variant_details, details?.bom_details, details?.service_details].forEach((rows) =>
    (rows ?? []).forEach((row) => add(row.ids)),
  );
  return names;
};

const toNamedIds = (ids: string[], names: Record<string, string>): IGiftNamedId[] =>
  ids.map((id) => ({ id, name: names[id] ?? "" }));

const toNumber = (value: string, fallback = 0): number => {
  const parsed = Number(value);
  return value.trim() !== "" && Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: string): number | null => (value.trim() === "" ? null : toNumber(value));

const toFieldString = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

const EMPTY_ID_CONDITION: IGiftIdConditionForm = {
  enabled: false,
  operation: "OR",
  min_ids_present: "0",
  included_ids: [],
  excluded_ids: [],
};

const EMPTY_ELIGIBILITY: IGiftEligibilityForm = {
  persona: EMPTY_ID_CONDITION,
  pincode: EMPTY_ID_CONDITION,
  clinic: EMPTY_ID_CONDITION,
  product: EMPTY_ID_CONDITION,
  category: EMPTY_ID_CONDITION,
  sub_category: EMPTY_ID_CONDITION,
  cart: { enabled: false, min_value: "", max_value: "" },
  event: { enabled: false, is_event_type_gift: false, condition_name: "", conditions: [] },
};

const ID_CONDITION_API_KEY = {
  persona: "persona_conditions",
  pincode: "pincode_conditions",
  clinic: "clinic_conditions",
  product: "product_conditions",
  category: "category_conditions",
  sub_category: "sub_category_conditions",
} as const satisfies Record<GiftIdConditionKey, keyof IGiftEligibilityConditions>;

const ID_CONDITION_KEYS = Object.keys(ID_CONDITION_API_KEY) as GiftIdConditionKey[];

// Conditions sent as {id, name}. The backend (qc_cart_service gifts_service ELIGIBILITY_CONDITION_ID_SOURCES)
// only converts these back to ids for clinic_conditions; product/category/sub_category must stay plain id strings
// or insert/update fails with "Input should be a valid string". Add them here once the backend lists them.
const NAMED_ID_CONDITION_KEYS: ReadonlySet<GiftIdConditionKey> = new Set(["clinic"]);

const toIdConditionForm = (condition: IGiftIdConditions | null | undefined): IGiftIdConditionForm =>
  condition
    ? {
        enabled: true,
        operation: condition.included_operation === "AND" ? "AND" : "OR",
        min_ids_present: toFieldString(condition.min_ids_present ?? 0),
        included_ids: toIdList(condition.included_ids),
        excluded_ids: toIdList(condition.excluded_ids),
      }
    : { ...EMPTY_ID_CONDITION };

const toEligibilityForm = (conditions: IGiftEligibilityConditions | null | undefined): IGiftEligibilityForm => {
  const cart = conditions?.cart_conditions;
  const event = conditions?.event_conditions;
  return {
    persona: toIdConditionForm(conditions?.persona_conditions),
    pincode: toIdConditionForm(conditions?.pincode_conditions),
    clinic: toIdConditionForm(conditions?.clinic_conditions),
    product: toIdConditionForm(conditions?.product_conditions),
    category: toIdConditionForm(conditions?.category_conditions),
    sub_category: toIdConditionForm(conditions?.sub_category_conditions),
    cart: cart
      ? { enabled: true, min_value: toFieldString(cart.min_value), max_value: toFieldString(cart.max_value) }
      : { ...EMPTY_ELIGIBILITY.cart },
    event: event
      ? {
          enabled: true,
          is_event_type_gift: Boolean(event.is_event_type_gift),
          condition_name: event.condition_name ?? "",
          conditions: event.conditions ?? [],
        }
      : { ...EMPTY_ELIGIBILITY.event },
  };
};

const toIdConditions = (
  condition: IGiftIdConditionForm,
  names: Record<string, string> | null,
): IGiftIdConditions | null =>
  condition.enabled
    ? {
        min_ids_present: toNumber(condition.min_ids_present),
        included_operation: condition.operation,
        included_ids: names ? toNamedIds(condition.included_ids, names) : condition.included_ids,
        excluded_ids: names ? toNamedIds(condition.excluded_ids, names) : condition.excluded_ids,
      }
    : null;

/** A switched-off condition is sent as null, which the backend treats as "no rule". */
const buildEligibilityConditions = (data: IGiftFormFields): IGiftEligibilityConditions => {
  const { cart, event } = data.eligibility;
  const idConditions = Object.fromEntries(
    ID_CONDITION_KEYS.map((key) => [
      ID_CONDITION_API_KEY[key],
      toIdConditions(data.eligibility[key], NAMED_ID_CONDITION_KEYS.has(key) ? data.id_names : null),
    ]),
  ) as Pick<IGiftEligibilityConditions, (typeof ID_CONDITION_API_KEY)[GiftIdConditionKey]>;

  return {
    ...idConditions,
    cart_conditions: cart.enabled
      ? { min_value: toNullableNumber(cart.min_value), max_value: toNullableNumber(cart.max_value) }
      : null,
    event_conditions: event.enabled
      ? {
          is_event_type_gift: event.is_event_type_gift,
          condition_name: event.condition_name.trim() || null,
          conditions: event.conditions,
        }
      : null,
  };
};

// Gift Details always shows one row per species (Dog, Cat), each switched on for a new gift.
const DEFAULT_ITEM_ROWS: IGiftItemFormRow[] = GIFT_PET_SPECIES_ITEMS.map((species) => ({
  pet_species_id: species.id,
  enabled: true,
  ids: [],
}));

/** Places saved rows onto the fixed species rows; a species the gift does not cover starts switched off. */
const toItemRows = (savedRows: IGiftItemDetails[]): IGiftItemFormRow[] =>
  GIFT_PET_SPECIES_ITEMS.map((species) => {
    const saved = savedRows.find((row) => String(row.pet_species_id) === species.id);
    return { pet_species_id: species.id, enabled: Boolean(saved), ids: toIdList(saved?.ids) };
  });

export const DEFAULT_VALUES: IGiftFormFields = {
  gift_name: "",
  gift_tag: "",
  status: "Active",
  start_date: "",
  end_date: "",
  limit_per_user: "1",
  total_usage_limit: "1",
  max_same_gift_types: "1",
  gift_priority: "0",
  gift_type: "variant",
  items: DEFAULT_ITEM_ROWS,
  coupon_id: "",
  max_quantity_count: "1",
  is_auto_addition: false,
  discount_type: "SellingPrice",
  discount_value: "",
  eligibility: EMPTY_ELIGIBILITY,
  id_names: {},
  tobe_claimed: true,
  claim_platform: "",
  allowed_claim_time: "",
  allowed_claim_time_unit: "days",
  allowed_avail_time: "",
  allowed_avail_time_unit: "days",
  add_platform: "app_qc_cart",
  avail_platform: "app_qc_cart",
  heading: "",
  sub_heading: "",
  gift_image: "",
};

/** Shows a saved number of seconds in the largest unit that divides it evenly (604800 → 7 days). */
const toDurationField = (seconds: number | null | undefined): { value: string; unit: GiftTimeUnit } => {
  if (!seconds) return { value: "", unit: "days" };
  const unit = (["days", "hours", "minutes"] as GiftTimeUnit[]).find(
    (candidate) => seconds % GIFT_TIME_UNIT_SECONDS[candidate] === 0,
  );
  return unit
    ? { value: String(seconds / GIFT_TIME_UNIT_SECONDS[unit]), unit }
    : { value: String(Math.round(seconds / 60)), unit: "minutes" };
};

/** Converts an entered duration to seconds for the backend; empty means no limit, sent as null. */
const toDurationSeconds = (value: string, unit: GiftTimeUnit): number | null => {
  const amount = toNullableNumber(value);
  return amount === null ? null : amount * GIFT_TIME_UNIT_SECONDS[unit];
};

const toClaimTimeFields = (
  claimConditions: IGift["claim_conditions"] | null | undefined,
): Pick<
  IGiftFormFields,
  "allowed_claim_time" | "allowed_claim_time_unit" | "allowed_avail_time" | "allowed_avail_time_unit"
> => {
  const claimTime = toDurationField(claimConditions?.allowed_claim_time);
  const availTime = toDurationField(claimConditions?.allowed_avail_time);
  return {
    allowed_claim_time: claimTime.value,
    allowed_claim_time_unit: claimTime.unit,
    allowed_avail_time: availTime.value,
    allowed_avail_time_unit: availTime.unit,
  };
};

/** Maps a `fetch` response onto the form, falling back to defaults for anything missing. */
export const toFormValues = (gift: IGift): IGiftFormFields => {
  const details = gift.gift_details;
  const giftType = isGiftType(gift.gift_type) ? gift.gift_type : DEFAULT_VALUES.gift_type;
  const itemDetails = details?.[GIFT_TYPE_DETAILS_KEY[giftType]] ?? [];
  const discountType = details?.discount_details?.discount_type;
  const eligibility = toEligibilityForm(gift.eligibility_conditions);

  return {
    gift_name: gift.gift_name ?? "",
    gift_tag: gift.gift_tag ?? "",
    status: gift.status === "Active" ? "Active" : "Inactive",
    start_date: toDateInput(gift.start_date),
    end_date: toDateInput(gift.end_date),
    limit_per_user: toFieldString(gift.limit_per_user ?? 1),
    total_usage_limit: toFieldString(gift.total_usage_limit ?? 1),
    max_same_gift_types: toFieldString(gift.max_same_gift_types ?? 1),
    gift_priority: toFieldString(gift.gift_priority ?? 0),
    gift_type: giftType,
    items: itemDetails.length ? toItemRows(itemDetails) : DEFAULT_ITEM_ROWS,
    coupon_id: details?.coupon_id ?? "",
    max_quantity_count: toFieldString(details?.max_quantity_count ?? 1),
    is_auto_addition: Boolean(details?.is_auto_addition),
    discount_type: discountType || DEFAULT_VALUES.discount_type,
    discount_value: toFieldString(details?.discount_details?.discount_value),
    eligibility,
    id_names: collectIdNames(gift),
    tobe_claimed: gift.claim_conditions?.tobe_claimed ?? true,
    claim_platform: gift.claim_conditions?.claim_platform ?? "",
    ...toClaimTimeFields(gift.claim_conditions),
    add_platform: gift.add_platform ?? "",
    avail_platform: gift.avail_platform ?? "",
    heading: gift.heading ?? "",
    sub_heading: gift.sub_heading ?? "",
    gift_image: gift.gift_image ?? "",
  };
};

// Only switched-on rows are sent; each of those is validated to have at least one id.
const toItemDetails = (items: IGiftItemFormRow[], names: Record<string, string>): IGiftItemDetails[] =>
  items
    .filter((row) => row.enabled)
    .map((row) => ({ pet_species_id: row.pet_species_id, ids: toNamedIds(row.ids, names) }));

/**
 * Builds the API payload from the form.
 * Server-owned fields (version, usage count, audit fields) are sent from `savedGift` but the backend ignores them.
 */
export const buildGiftPayload = (data: IGiftFormFields, savedGift?: IGift | null): IGift => {
  const itemDetails = toItemDetails(data.items, data.id_names);

  return {
    gift_id: savedGift?.gift_id ?? null,
    gift_name: data.gift_name.trim(),
    gift_tag: data.gift_tag.trim(),
    version: savedGift?.version ?? 2,
    status: data.status,
    start_date: data.start_date,
    end_date: data.end_date,
    limit_per_user: toNumber(data.limit_per_user),
    total_usage_count: savedGift?.total_usage_count ?? 0,
    total_usage_limit: toNumber(data.total_usage_limit, 1),
    gift_type: data.gift_type,
    max_same_gift_types: toNumber(data.max_same_gift_types),
    gift_priority: toNumber(data.gift_priority),
    eligibility_conditions: buildEligibilityConditions(data),
    gift_details: {
      variant_details: data.gift_type === "variant" ? itemDetails : null,
      bom_details: data.gift_type === "bom" ? itemDetails : null,
      service_details: data.gift_type === "service" ? itemDetails : null,
      // Any gift type can carry a coupon; it is required only for service gifts (checked in the form).
      coupon_id: data.coupon_id.trim() || null,
      max_quantity_count: toNumber(data.max_quantity_count, 1),
      is_auto_addition: data.is_auto_addition,
      discount_details: {
        discount_type: data.discount_type,
        discount_value: toNumber(data.discount_value),
      },
    },
    claim_conditions: {
      // The switch only sets this flag; claim platform and claim time are sent the same either way.
      tobe_claimed: data.tobe_claimed,
      allowed_claim_time: toDurationSeconds(data.allowed_claim_time, data.allowed_claim_time_unit),
      allowed_avail_time: toDurationSeconds(data.allowed_avail_time, data.allowed_avail_time_unit),
      claim_platform: data.claim_platform || null,
    },
    add_platform: data.add_platform,
    avail_platform: data.avail_platform,
    heading: data.heading.trim(),
    sub_heading: data.sub_heading.trim(),
    gift_image: data.gift_image.trim(),
  };
};
